import React from 'react';
import AuthenticatedInput from '../AuthenticatedInput';
import { updateExamField } from '../../utils/examData';
import {
  calculateHipFlexion, calculateHipExtensionContracture, calculateHipInternalRotation,
  calculateHipExternalRotation, calculateHipAdduction, calculateHipAbductionContracture,
} from '../TabRatingNarratives';
import { useExamTab } from './ExamTabContext';
import JointReplacementPanel from './JointReplacementPanel';
import { THA, replacementRating } from '../../utils/jointReplacement';

const HipsTab: React.FC = () => {
  const {
    examData, setExamData, user, userProfile, startManualSession, setShowAuthModal,
    copyRegionValues, handleClearRegion, clearConfirmRegion, fillNormalValues,
  } = useExamTab();

  return (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Hips</h3>

                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">

                  {/* Hip Rating Summary — live WPI instrument (right column) */}
                  <div className="lg:order-2 lg:sticky lg:top-20">
                    <div className="card p-4 flex flex-col gap-4">
                      <div>
                        <span className="font-medium">Right Hip WPI:</span>
                        <span className="ml-2 text-xl font-bold">
                        {(() => {
                          const data = examData?.hips;
                          if (!data) return "--";
                          // A replaced hip shows its THA score, as the report does.
                          const tha = replacementRating(THA, data, 'right');
                          if (tha) return `${tha.wpi}% (THA)`;
                          
                          // Check if right hip has ROM values
                          if (data.right_flexion === null && 
                              data.right_extension_contracture === null && 
                              data.right_internal_rotation === null && 
                              data.right_external_rotation === null &&
                              data.right_adduction === null &&
                              data.right_abduction_contracture === null) {
                            return "--";
                          }
                          
                          // Calculate individual impairments
                          const rightFlexion = calculateHipFlexion(data.right_flexion ?? null);
                          const rightExtensionContracture = calculateHipExtensionContracture(data.right_extension_contracture ?? null);
                          const rightInternalRotation = calculateHipInternalRotation(data.right_internal_rotation ?? null);
                          const rightExternalRotation = calculateHipExternalRotation(data.right_external_rotation ?? null);
                          const rightAdduction = calculateHipAdduction(data.right_adduction ?? null);
                          const rightAbductionContracture = calculateHipAbductionContracture(data.right_abduction_contracture ?? null);
                          
                          // Sum individual impairments
                          const rightHipRating = rightFlexion + rightExtensionContracture + 
                                               rightInternalRotation + rightExternalRotation + 
                                               rightAdduction + rightAbductionContracture;
                          
                          // Convert to Whole Person Impairment (40% of lower extremity rating)
                          const rightWPI = Math.round(rightHipRating * 0.4);
                          return `${rightWPI}%`;
                        })()
                        }
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Left Hip WPI:</span>
                        <span className="ml-2 text-xl font-bold">
                        {(() => {
                          const data = examData?.hips;
                          if (!data) return "--";
                          // A replaced hip shows its THA score, as the report does.
                          const tha = replacementRating(THA, data, 'left');
                          if (tha) return `${tha.wpi}% (THA)`;
                          
                          // Check if left hip has ROM values
                          if (data.left_flexion === null && 
                              data.left_extension_contracture === null && 
                              data.left_internal_rotation === null && 
                              data.left_external_rotation === null &&
                              data.left_adduction === null &&
                              data.left_abduction_contracture === null) {
                            return "--";
                          }
                          
                          // Calculate individual impairments
                          const leftFlexion = calculateHipFlexion(data.left_flexion ?? null);
                          const leftExtensionContracture = calculateHipExtensionContracture(data.left_extension_contracture ?? null);
                          const leftInternalRotation = calculateHipInternalRotation(data.left_internal_rotation ?? null);
                          const leftExternalRotation = calculateHipExternalRotation(data.left_external_rotation ?? null);
                          const leftAdduction = calculateHipAdduction(data.left_adduction ?? null);
                          const leftAbductionContracture = calculateHipAbductionContracture(data.left_abduction_contracture ?? null);
                          
                          // Sum individual impairments
                          const leftHipRating = leftFlexion + leftExtensionContracture + 
                                              leftInternalRotation + leftExternalRotation + 
                                              leftAdduction + leftAbductionContracture;
                          
                          // Convert to Whole Person Impairment (40% of lower extremity rating)
                          const leftWPI = Math.round(leftHipRating * 0.4);
                          return `${leftWPI}%`;
                        })()
                        }
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Range of Motion Section — left column */}
                  <div className="lg:order-1 mb-0">
                    <div className="flex justify-between items-center mb-4 border-b border-border pb-2">
                      <h4 className="text-lg font-medium">Range of Motion</h4>
                      <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('hips', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('hips', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('hips')} className={clearConfirmRegion === 'hips' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'hips' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('hips')} className="btn-secondary text-xs" disabled={!user}>Fill Normal Values</button></div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                      {/* Header Row */}
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                      <div className="col-span-1 text-center font-medium">Right</div>
                      <div className="col-span-1 text-center font-medium">Left</div>

                      {/* Flexion Row */}
                      <div className="col-span-1 text-content-secondary">Flexion</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.right_flexion ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'right_flexion', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.left_flexion ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'left_flexion', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>

                      {/* Extension Contracture Row */}
                      <div className="col-span-1 text-content-secondary">Extension Contracture</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.right_extension_contracture ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'right_extension_contracture', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.left_extension_contracture ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'left_extension_contracture', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>

                      {/* Internal Rotation Row */}
                      <div className="col-span-1 text-content-secondary">Internal Rotation</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">40°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.right_internal_rotation ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'right_internal_rotation', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.left_internal_rotation ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'left_internal_rotation', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>

                      {/* External Rotation Row */}
                      <div className="col-span-1 text-content-secondary">External Rotation</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">50°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.right_external_rotation ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'right_external_rotation', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.left_external_rotation ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'left_external_rotation', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>

                      {/* Adduction Row */}
                      <div className="col-span-1 text-content-secondary">Adduction</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">20°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.right_adduction ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'right_adduction', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.left_adduction ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'left_adduction', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>

                      {/* Abduction Contracture Row */}
                      <div className="col-span-1 text-content-secondary">Abduction Contracture</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.right_abduction_contracture ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'right_abduction_contracture', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput type="number" className="input-field text-center" value={examData?.hips?.left_abduction_contracture ?? ''} onChange={(value) => { setExamData(prev => updateExamField(prev, 'hips', 'left_abduction_contracture', value)); }} user={user} userProfile={userProfile} onSessionStart={startManualSession} onShowAuthModal={() => setShowAuthModal(true)} />
                      </div>
                    </div>
                  </div>
                  </div>

                  <JointReplacementPanel config={THA} />
                </div>
  );
};

export default HipsTab;
